export interface TeamPalette {
  primary: number;
  secondary: number;
  tertiary: number;
  rgb: {
    r: number;
    g: number;
    b: number;
  };
}

export interface ReplayTeamColors {
  home: TeamPalette;
  away: TeamPalette;
}

export interface KitColors {
  primary?: string | null;
  secondary?: string | null;
  tertiary?: string | null;
}

export interface KitStyles {
  team_a?: { colors?: KitColors };
  team_b?: { colors?: KitColors };
}

const DEFAULT_HOME_PRIMARY = 0x067aff; // Blue
const DEFAULT_HOME_SECONDARY = 0xffffff;
const DEFAULT_HOME_TERTIARY = 0x000000;

const colorStringCache = new Map<string, number>();

const toRgb = (color: number) => ({
  r: (color >> 16) & 0xff,
  g: (color >> 8) & 0xff,
  b: color & 0xff,
});

const rgbToHsv = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, v };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  let r = 0,
    g = 0,
    b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      (r = v), (g = t), (b = p);
      break;
    case 1:
      (r = q), (g = v), (b = p);
      break;
    case 2:
      (r = p), (g = v), (b = t);
      break;
    case 3:
      (r = p), (g = q), (b = v);
      break;
    case 4:
      (r = t), (g = p), (b = v);
      break;
    case 5:
      (r = v), (g = p), (b = q);
      break;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

const rotateColor = (color: number): number => {
  const { r, g, b } = toRgb(color);
  let { h, s, v } = rgbToHsv(r, g, b);

  let rotatedHue = (h + 0.4) % 1.0;
  let rotatedSaturation = s;
  let rotatedValue = v;

  if (s < 0.3 || v < 0.3) {
    rotatedValue = v > 0.5 ? 0.1 : 0.9;

    if (Math.abs(v - 0.5) < 0.2) {
      rotatedSaturation = 0.7;
    }
  }

  const { r: nr, g: ng, b: nb } = hsvToRgb(rotatedHue, rotatedSaturation, rotatedValue);
  return (nr << 16) | (ng << 8) | nb;
};

const parseColor = (color: string) => {
  if (typeof document === "undefined") {
    return null;
  }

  const normalized = color.trim().toLowerCase();
  const cached = colorStringCache.get(normalized);
  if (cached !== undefined) {
    return cached;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  context.fillStyle = "#000000";
  context.fillStyle = normalized;

  const resolved = context.fillStyle;
  if (typeof resolved !== "string" || !resolved.startsWith("#")) {
    return null;
  }

  const hex = resolved.slice(1);
  const parsed = Number.parseInt(hex, 16);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  colorStringCache.set(normalized, parsed);
  return parsed;
};

const resolveColorValue = (color: string | null | undefined, fallback: number) => {
  if (!color || !color.trim()) {
    return fallback;
  }

  return parseColor(color) ?? fallback;
};

const createPalette = (p?: string | null, s?: string | null, t?: string | null, fallbacks = { p: 0, s: 0, t: 0 }): TeamPalette => {
  const primary = resolveColorValue(p, fallbacks.p);
  return {
    primary,
    secondary: resolveColorValue(s, fallbacks.s),
    tertiary: resolveColorValue(t, fallbacks.t),
    rgb: toRgb(primary),
  };
};

export const resolveReplayTeamColors = (homeKit?: KitColors, styles?: KitStyles): ReplayTeamColors => {
  const home = createPalette(homeKit?.primary, homeKit?.secondary, homeKit?.tertiary, {
    p: DEFAULT_HOME_PRIMARY,
    s: DEFAULT_HOME_SECONDARY,
    t: DEFAULT_HOME_TERTIARY,
  });

  const away = (() => {
    const primary = rotateColor(home.primary);
    const secondary = rotateColor(home.secondary);
    const tertiary = rotateColor(home.tertiary);
    return {
      primary,
      secondary,
      tertiary,
      rgb: toRgb(primary),
    };
  })();

  return {
    home: createPalette(styles?.team_a?.colors?.primary, styles?.team_a?.colors?.secondary, styles?.team_a?.colors?.tertiary, {
      p: home.primary,
      s: home.secondary,
      t: home.tertiary,
    }),
    away: createPalette(styles?.team_b?.colors?.primary, styles?.team_b?.colors?.secondary, styles?.team_b?.colors?.tertiary, {
      p: away.primary,
      s: away.secondary,
      t: away.tertiary,
    }),
  };
};

export const DEFAULT_TEAM_COLORS = resolveReplayTeamColors();
